import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { analyze, formatUsd, settlementWeek } from "@haulsheet/core";
import { getDb } from "../db/dexie";
import { contractForLoad } from "../db/contracts";
import { buildAnalyzeInput } from "../hooks/useAnalyzeLoad";
import { useAuthStore } from "../store/authStore";
import { useSyncStore } from "../store/syncStore";
import {
  SETTLEMENT_STATUS_LABEL,
  formatPeriod,
  listSettlements,
  type SettlementSummary,
} from "../api/settlements";

/**
 * Money: totals from the loads on this phone (work offline), plus the pay
 * weeks (settlements) from the server. A delivered load joins its week's open
 * settlement; the owner finalizes it and marks it paid, which pays the loads.
 */
export function MoneyScreen() {
  const me = useAuthStore((s) => s.me);
  const tz = me?.org.timezone ?? "UTC";
  const totals = useLiveQuery(async () => {
    const db = getDb();
    const loads = (await db.load.toArray()).filter(
      (l) => l.deletedAt == null && (l.status === "delivered" || l.status === "paid"),
    );
    const contracts = await db.pay_contract.toArray();
    const costs = await db.cost_profile.toArray();
    const trucks = await db.truck.toArray();
    const cost = costs.find((c) => c.driverId === me?.driverId) ?? costs[0] ?? null;
    const truck = trucks[0] ?? null;
    const thisWeek = settlementWeek(new Date(), tz);
    const inWeek = (iso: string | null) => !!iso && settlementWeek(iso, tz).periodStart === thisWeek.periodStart;
    const sum = { week: { n: 0, keep: 0, paid: 0 }, unpaid: { n: 0, keep: 0 }, paid: { n: 0, keep: 0 }, weekDetention: 0 };
    // Server-side changes that move a pay week (attach on delivered, paid).
    const payKey = loads.map((l) => `${l.id}:${l.status}:${l.settlementId ?? ""}`).sort().join("|");
    for (const l of loads) {
      const keep = analyze(buildAnalyzeInput(l, contractForLoad(l, contracts), cost, truck)).keepCents;
      if (l.status === "paid") {
        sum.paid.n++;
        sum.paid.keep += keep;
      } else {
        sum.unpaid.n++;
        sum.unpaid.keep += keep;
      }
      if (inWeek(l.deliveredAt)) {
        sum.week.n++;
        sum.week.keep += keep;
        if (l.status === "paid") sum.week.paid++;
      }
    }
    for (const d of await db.detention.toArray()) {
      if (d.deletedAt == null && d.departedAt && d.payCents && inWeek(d.departedAt)) sum.weekDetention += d.payCents;
    }
    return { ...sum, period: thisWeek, payKey };
  }, [me?.driverId, tz]);

  const { settlements, offline } = usePayWeeks(totals?.payKey);

  return (
    <div className="stack">
      <h1 className="screen-title">Money</h1>
      <div className="card stack" data-testid="money-totals">
        <div className="list-item" style={{ padding: 0 }}>
          <div>
            <div>This week</div>
            <div className="muted">
              {totals ? formatPeriod(totals.period.periodStart, totals.period.periodEnd) : ""} ·{" "}
              <span data-testid="money-week-count">{weekCountLabel(totals?.week)}</span>
              {totals && totals.weekDetention > 0 ? ` · ${formatUsd(totals.weekDetention)} detention` : ""}
            </div>
          </div>
          <strong data-testid="money-week-keep">{formatUsd((totals?.week.keep ?? 0) + (totals?.weekDetention ?? 0))}</strong>
        </div>
        <div className="list-item" style={{ padding: 0 }}>
          <div>
            <div>Waiting on pay</div>
            <div className="muted">{totals?.unpaid.n ?? 0} delivered, not paid yet</div>
          </div>
          <strong data-testid="money-unpaid-keep">{formatUsd(totals?.unpaid.keep ?? 0)}</strong>
        </div>
        <div className="list-item" style={{ padding: 0 }}>
          <div>
            <div>Paid</div>
            <div className="muted">{totals?.paid.n ?? 0} loads</div>
          </div>
          <strong data-testid="money-paid-keep">{formatUsd(totals?.paid.keep ?? 0)}</strong>
        </div>
        <p className="muted" style={{ margin: 0 }}>Amounts are what you keep after the cut.</p>
      </div>

      <h2 style={{ margin: 0, fontSize: "1.05rem" }}>Pay weeks</h2>
      <div className="card" style={{ padding: 0 }}>
        {settlements && settlements.length > 0 ? (
          settlements.map((s) => (
            <Link key={s.id} to={`/money/settlements/${s.id}`} className="list-item" data-testid="settlement-row">
              <div>
                <div>{formatPeriod(s.periodStart, s.periodEnd)}</div>
                <div className="muted">
                  <span className="chip">{SETTLEMENT_STATUS_LABEL[s.status] ?? s.status}</span> {s.totals.loadCount}{" "}
                  {s.totals.loadCount === 1 ? "load" : "loads"}
                </div>
              </div>
              <strong>{formatUsd(s.totals.totalCents)}</strong>
            </Link>
          ))
        ) : settlements ? (
          <p className="muted" style={{ padding: "1rem", margin: 0 }} data-testid="settlements-empty">
            No pay weeks yet. Deliver a load and it goes on this week&apos;s pay.
          </p>
        ) : offline ? (
          <p className="muted" style={{ padding: "1rem", margin: 0 }} data-testid="settlements-offline">
            Pay weeks show here when you&apos;re online.
          </p>
        ) : (
          <p className="muted" style={{ padding: "1rem", margin: 0 }} data-testid="settlements-loading" aria-busy="true">
            Loading pay weeks…
          </p>
        )}
      </div>
    </div>
  );
}

/** "1 delivered · paid", "2 delivered · 1 paid", "1 delivered · waiting on pay". */
export function weekCountLabel(week: { n: number; paid: number } | undefined): string {
  const n = week?.n ?? 0;
  const paid = week?.paid ?? 0;
  if (n === 0) return "0 delivered";
  if (paid === n) return `${n} delivered · paid`;
  if (paid > 0) return `${n} delivered · ${paid} paid`;
  return `${n} delivered · waiting on pay`;
}

/**
 * Pay weeks from the server. A load's attach to its week happens on the server
 * when its "delivered" push lands, so the list is refetched when that lands
 * (load status / pay week changes locally), whenever a sync finishes with an
 * empty outbox, and when the app comes back to the front. The newest request
 * wins; a slow older answer never overwrites it. Until the first answer the
 * list says it's loading, never "No pay weeks yet".
 */
export function usePayWeeks(payKey: string | undefined): { settlements: SettlementSummary[] | null; offline: boolean } {
  const [settlements, setSettlements] = useState<SettlementSummary[] | null>(null);
  const [offline, setOffline] = useState(false);
  const seq = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refetch = useCallback(() => {
    const mine = ++seq.current;
    listSettlements()
      .then((r) => {
        if (!mounted.current || mine !== seq.current) return;
        setSettlements(r.items);
        setOffline(false);
      })
      .catch(() => {
        if (mounted.current && mine === seq.current) setOffline(true);
      });
  }, []);

  // Mount, and every time a load is delivered / attached / paid here.
  useEffect(() => {
    refetch();
  }, [payKey, refetch]);

  // A sync that leaves nothing queued: the server has everything we did.
  const lastSyncAt = useSyncStore((s) => s.lastSyncAt);
  const drained = useSyncStore((s) => s.pendingCount === 0);
  const firstSync = useRef(true);
  useEffect(() => {
    if (firstSync.current) {
      firstSync.current = false;
      return;
    }
    if (drained) refetch();
  }, [lastSyncAt, drained, refetch]);

  // Back to the app.
  useEffect(() => {
    const onFocus = () => refetch();
    const onVisible = () => {
      if (document.visibilityState === "visible") refetch();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refetch]);

  return { settlements, offline };
}
